import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

export class ScrollController {
  private readonly lenis: Lenis;
  private trigger?: ScrollTrigger;
  private manualSelection?: number;
  private readonly resumeStory = () => {
    this.manualSelection = undefined;
  };
  private readonly resumeKey = (e: KeyboardEvent) => {
    if (['PageDown', 'PageUp', ' '].includes(e.key)) this.resumeStory();
  };
  readonly reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  private readonly tick = (seconds: number) => this.lenis.raf(seconds * 1000);
  constructor(private readonly onProgress: (progress: number) => void) {
    this.lenis = new Lenis({ duration: 0.85, smoothWheel: !this.reduced, anchors: { offset: -92 } });
    this.lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(this.tick);
    gsap.ticker.lagSmoothing(0);
    addEventListener('wheel', this.resumeStory, { passive: true });
    addEventListener('touchstart', this.resumeStory, { passive: true });
    addEventListener('keydown', this.resumeKey);
  }
  mount(): void {
    this.trigger?.kill();
    this.trigger = undefined;
    this.lenis.resize();
    this.manualSelection = undefined;
    this.onProgress(0);
    const story = document.querySelector<HTMLElement>('[data-story]');
    if (story && !this.reduced) {
      this.trigger = ScrollTrigger.create({
        trigger: story,
        start: 'top top',
        end: 'bottom bottom',
        onUpdate: (self) => {
          if (this.manualSelection === undefined) this.onProgress(self.progress);
        },
      });
    }
    ScrollTrigger.refresh();
  }
  selectPhase(index: number, immediate = false): void {
    const progress = index / 5;
    this.manualSelection = progress;
    this.onProgress(progress);
    if (this.trigger) {
      this.lenis.scrollTo(this.trigger.start + (this.trigger.end - this.trigger.start) * progress, {
        duration: this.reduced ? 0 : 0.7,
        immediate,
        force: true,
      });
    }
  }
  restoreStudy(): void {
    ScrollTrigger.refresh();
    this.selectPhase(5, true);
    if (!this.trigger) {
      const study = document.getElementById('construction');
      if (study) this.scrollTo(study, true);
    }
  }
  scrollTo(target: number | HTMLElement, immediate = false): void {
    this.lenis.scrollTo(target, { immediate, force: true, offset: typeof target === 'number' ? 0 : -92 });
  }
  stop(): void {
    this.lenis.stop();
  }
  start(): void {
    this.lenis.start();
  }
  destroy(): void {
    this.trigger?.kill();
    gsap.ticker.remove(this.tick);
    this.lenis.destroy();
    removeEventListener('wheel', this.resumeStory);
    removeEventListener('touchstart', this.resumeStory);
    removeEventListener('keydown', this.resumeKey);
  }
}
